import React, { useEffect, useState, useCallback } from 'react';
import {
    Button,
    Chip,
    IconButton,
    Input,
    Option,
    Select,
    Spinner,
    Textarea,
} from '@material-tailwind/react';
import {
    DownloadIcon,
    Edit2Icon,
    PlusIcon,
    Trash2Icon,
    FileTextIcon,
    ArrowLeftIcon,
} from 'lucide-react';
import moment from 'moment';
import {
    CreateQuotation,
    DeleteQuotation,
    GetQuotationsByLead,
    UpdateQuotation,
} from '../../../services/backend';
import { downloadQuotationPdf } from '../../../utils/quotation-pdf';
import { Notify, NotifyError } from '../../../utils/functions';
import {
    CURRENCIES,
    CURRENCIES_ENUM,
    QUOTATION_STATUSES,
    QUOTATION_STATUSES_ENUM,
    QUOTATION_UNITS,
    QUOTATION_UNITS_ENUM,
} from '../../../utils/enums';
import { convertToPresentableText } from '../../../utils/functions';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import { useGetQuotationDefaults } from '../../../custom-hooks/use-settings';

const STATUS_COLORS = {
    [QUOTATION_STATUSES.DRAFT]: 'blue-gray',
    [QUOTATION_STATUSES.SENT]: 'blue',
    [QUOTATION_STATUSES.ACCEPTED]: 'green',
    [QUOTATION_STATUSES.REJECTED]: 'red',
    [QUOTATION_STATUSES.EXPIRED]: 'amber',
};

const buildAddressFromLead = (lead) => {
    const a = lead?.companyAddress || {};
    return [a.street, a.city, a.state, a.postalCode, a.country].filter(Boolean).join(', ');
};

const buildInitialFormData = (lead, defaults) => ({
    status: QUOTATION_STATUSES.DRAFT,
    issueDate: moment().format('YYYY-MM-DD'),
    validUntil: moment().add(7, 'days').format('YYYY-MM-DD'),
    customer: {
        fullName: lead?.fullName || '',
        company: lead?.companyName || '',
        email: lead?.emailAddress || '',
        phone: `${lead?.countryCode || ''}${lead?.phoneNumber || ''}`,
        address: buildAddressFromLead(lead),
    },
    lineItems: [
        { unit: QUOTATION_UNITS.SEAT, quantity: 1, unitPrice: 0, seatsPerOffice: '', area: '' },
    ],
    pricing: {
        discount: { type: '', value: 0, reason: '' },
        taxPercent: 0,
        securityDeposit: 0,
        advance: 0,
        currency: CURRENCIES.PKR,
    },
    terms: {
        inclusions: defaults?.inclusions || '',
        termsAndConditions: defaults?.termsAndConditions || '',
        notes: defaults?.notes || '',
        exclusions: '',
    },
});

const computeLineTotal = (item) => {
    const qty = Number(item?.quantity || 0);
    const price = Number(item?.unitPrice || 0);
    if (item?.unit === QUOTATION_UNITS.OFFICE) {
        const seats = Number(item?.seatsPerOffice || 0);
        return qty * seats * price;
    }
    return qty * price;
};

const computeSubtotal = (lineItems = []) => lineItems.reduce((sum, item) => sum + computeLineTotal(item), 0);

const computeDiscountAmount = (subtotal, discount = {}) => {
    if (!discount?.type) return 0;
    const value = Number(discount.value || 0);
    if (discount.type === 'percent') return (subtotal * value) / 100;
    if (discount.type === 'flat') return value;
    return 0;
};

const formatNumber = (n, currency) => `${currency} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const QuotationTab = ({ lead, leadId }) => {
    const { data: quotationDefaults } = useGetQuotationDefaults();
    const [quotations, setQuotations] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [mode, setMode] = useState('list'); // 'list' | 'form'
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(() => buildInitialFormData(lead, quotationDefaults));
    const [saving, setSaving] = useState(false);
    const { confirm, ConfirmationDialog } = useConfirm();

    const fetchQuotations = useCallback(async () => {
        if (!leadId) return;
        setListLoading(true);
        try {
            const res = await GetQuotationsByLead(leadId);
            setQuotations(res.data.quotations || []);
        } catch (err) {
            NotifyError('Failed to fetch quotations', err);
        } finally {
            setListLoading(false);
        }
    }, [leadId]);

    useEffect(() => {
        fetchQuotations();
    }, [fetchQuotations]);

    if (!leadId) {
        return (
            <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                <FileTextIcon size={40} className="mb-3" />
                <p className="text-sm">Save the lead first to start creating quotations.</p>
            </div>
        );
    }

    const openNewForm = () => {
        setEditingId(null);
        setFormData(buildInitialFormData(lead, quotationDefaults));
        setMode('form');
    };

    const openEditForm = (q) => {
        setEditingId(q._id);
        setFormData({
            status: q.status || QUOTATION_STATUSES.DRAFT,
            issueDate: q.issueDate ? moment(q.issueDate).format('YYYY-MM-DD') : '',
            validUntil: q.validUntil ? moment(q.validUntil).format('YYYY-MM-DD') : '',
            customer: {
                fullName: q.customer?.fullName || '',
                company: q.customer?.company || '',
                email: q.customer?.email || '',
                phone: q.customer?.phone || '',
                address: q.customer?.address || '',
            },
            lineItems: (q.lineItems || []).map((item) => ({
                unit: item.unit || QUOTATION_UNITS.SEAT,
                quantity: item.quantity || 0,
                unitPrice: item.unitPrice || 0,
                seatsPerOffice: item.seatsPerOffice ?? '',
                area: item.area || '',
            })),
            pricing: {
                discount: {
                    type: q.pricing?.discount?.type || '',
                    value: q.pricing?.discount?.value || 0,
                    reason: q.pricing?.discount?.reason || '',
                },
                taxPercent: q.pricing?.taxPercent || 0,
                securityDeposit: q.pricing?.securityDeposit || 0,
                advance: q.pricing?.advance || 0,
                currency: q.pricing?.currency || CURRENCIES.PKR,
            },
            terms: {
                inclusions: q.terms?.inclusions || '',
                termsAndConditions: q.terms?.termsAndConditions || '',
                notes: q.terms?.notes || '',
                exclusions: q.terms?.exclusions || '',
            },
        });
        setMode('form');
    };

    const closeForm = () => {
        setMode('list');
        setEditingId(null);
    };

    const updateLineItem = (idx, field, value) => {
        setFormData((prev) => ({
            ...prev,
            lineItems: prev.lineItems.map((item, i) => {
                if (i !== idx) return item;
                const next = { ...item, [field]: value };
                if (field === 'unit' && value !== QUOTATION_UNITS.OFFICE) {
                    next.seatsPerOffice = '';
                }
                return next;
            }),
        }));
    };

    const addLineItem = () => {
        setFormData((prev) => ({
            ...prev,
            lineItems: [
                ...prev.lineItems,
                { unit: QUOTATION_UNITS.SEAT, quantity: 1, unitPrice: 0, seatsPerOffice: '', area: '' },
            ],
        }));
    };

    const removeLineItem = (idx) => {
        setFormData((prev) => ({
            ...prev,
            lineItems: prev.lineItems.filter((_, i) => i !== idx),
        }));
    };

    const updatePricing = (field, value) => {
        setFormData((prev) => ({ ...prev, pricing: { ...prev.pricing, [field]: value } }));
    };

    const updateDiscount = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            pricing: { ...prev.pricing, discount: { ...prev.pricing.discount, [field]: value } },
        }));
    };

    const updateTerms = (field, value) => {
        setFormData((prev) => ({ ...prev, terms: { ...prev.terms, [field]: value } }));
    };

    const updateCustomer = (field, value) => {
        setFormData((prev) => ({ ...prev, customer: { ...prev.customer, [field]: value } }));
    };

    const subtotal = computeSubtotal(formData.lineItems);
    const discountAmount = computeDiscountAmount(subtotal, formData.pricing.discount);
    const afterDiscount = Math.max(0, subtotal - discountAmount);
    const taxAmount = (afterDiscount * Number(formData.pricing.taxPercent || 0)) / 100;
    const grandTotal = afterDiscount
        + taxAmount
        + Number(formData.pricing.securityDeposit || 0)
        - Number(formData.pricing.advance || 0);

    const buildPayload = () => ({
        ...formData,
        lineItems: formData.lineItems.map((item) => ({
            unit: item.unit,
            quantity: Number(item.quantity || 0),
            unitPrice: Number(item.unitPrice || 0),
            seatsPerOffice: item.seatsPerOffice === '' || item.seatsPerOffice === null
                ? undefined
                : Number(item.seatsPerOffice),
            area: item.area || undefined,
        })),
        pricing: {
            ...formData.pricing,
            taxPercent: Number(formData.pricing.taxPercent || 0),
            securityDeposit: Number(formData.pricing.securityDeposit || 0),
            advance: Number(formData.pricing.advance || 0),
            discount: {
                ...formData.pricing.discount,
                value: Number(formData.pricing.discount.value || 0),
            },
        },
        validUntil: formData.validUntil || undefined,
        issueDate: formData.issueDate || undefined,
    });

    const handleSave = async ({ andDownload = false } = {}) => {
        if (formData.lineItems.length === 0) {
            NotifyError('Please add at least one line item');
            return;
        }
        const invalidIdx = formData.lineItems.findIndex(
            (it) => !it.unit || !(Number(it.quantity) > 0) || Number(it.unitPrice) < 0
        );
        if (invalidIdx !== -1) {
            NotifyError(`Line item ${invalidIdx + 1} is incomplete`);
            return;
        }
        const officeMissingSeatsIdx = formData.lineItems.findIndex(
            (it) => it.unit === QUOTATION_UNITS.OFFICE && !(Number(it.seatsPerOffice) > 0)
        );
        if (officeMissingSeatsIdx !== -1) {
            NotifyError(`Line item ${officeMissingSeatsIdx + 1} (office): enter seats per office`);
            return;
        }

        setSaving(true);
        try {
            const payload = buildPayload();
            let saved;
            if (editingId) {
                const res = await UpdateQuotation(editingId, payload);
                saved = res.data.quotation;
                Notify('Quotation updated successfully', 'success');
            } else {
                const res = await CreateQuotation(leadId, payload);
                saved = res.data.quotation;
                Notify('Quotation created successfully', 'success');
            }
            await fetchQuotations();
            if (andDownload && saved) {
                await downloadQuotationPdf({ quotation: saved });
            }
            closeForm();
        } catch (err) {
            NotifyError('Failed to save quotation', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (q) => {
        const ok = await confirm({
            title: 'Delete Quotation',
            message: `Delete quotation ${q.quotationNumber}? This cannot be undone.`,
            confirmColor: 'red',
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });
        if (!ok) return;
        try {
            await DeleteQuotation(q._id);
            Notify('Quotation deleted', 'success');
            fetchQuotations();
        } catch (err) {
            NotifyError('Failed to delete quotation', err);
        }
    };

    const handleDownload = async (q) => {
        try {
            await downloadQuotationPdf({ quotation: q });
        } catch (err) {
            NotifyError('Failed to generate PDF', err);
        }
    };

    if (mode === 'list') {
        return (
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700">Quotations ({quotations.length})</h3>
                    <Button size="sm" className="rounded-full flex items-center gap-1" onClick={openNewForm}>
                        <PlusIcon size={14} /> New Quotation
                    </Button>
                </div>

                {listLoading ? (
                    <div className="flex justify-center py-12"><Spinner /></div>
                ) : quotations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-500 border border-dashed rounded-lg">
                        <FileTextIcon size={36} className="mb-2" />
                        <p className="text-sm">No quotations yet for this lead.</p>
                        <p className="text-xs">Click "New Quotation" to create one.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {quotations.map((q) => (
                            <div key={q._id} className="border rounded-lg p-3 flex items-center justify-between gap-3 hover:bg-gray-50">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-sm">{q.quotationNumber}</span>
                                        <Chip
                                            size="sm"
                                            variant="ghost"
                                            color={STATUS_COLORS[q.status] || 'gray'}
                                            value={convertToPresentableText(q.status)}
                                            className="rounded-full text-[10px]"
                                        />
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {moment(q.issueDate).format('MMM DD, YYYY')}
                                        {q.validUntil && ` · valid until ${moment(q.validUntil).format('MMM DD, YYYY')}`}
                                    </div>
                                    <div className="text-sm font-medium mt-1">
                                        {formatNumber(q.pricing?.total, q.pricing?.currency || 'PKR')}
                                        <span className="text-xs text-gray-500 font-normal ml-2">
                                            · {q.lineItems?.length || 0} item(s)
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <IconButton size="sm" variant="text" color="blue" onClick={() => handleDownload(q)} title="Download PDF">
                                        <DownloadIcon size={16} />
                                    </IconButton>
                                    <IconButton size="sm" variant="text" color="amber" onClick={() => openEditForm(q)} title="Edit">
                                        <Edit2Icon size={16} />
                                    </IconButton>
                                    <IconButton size="sm" variant="text" color="red" onClick={() => handleDelete(q)} title="Delete">
                                        <Trash2Icon size={16} />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <ConfirmationDialog />
            </div>
        );
    }

    // Form mode
    const currency = formData.pricing.currency;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Button size="sm" variant="text" className="flex items-center gap-1" onClick={closeForm} type="button">
                    <ArrowLeftIcon size={14} /> Back to list
                </Button>
                <h3 className="text-sm font-semibold text-gray-700">
                    {editingId ? 'Edit Quotation' : 'New Quotation'}
                </h3>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                    label="Issue Date"
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) => setFormData((p) => ({ ...p, issueDate: e.target.value }))}
                />
                <Input
                    label="Valid Until"
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData((p) => ({ ...p, validUntil: e.target.value }))}
                />
                <Select
                    label="Status"
                    value={formData.status}
                    onChange={(value) => setFormData((p) => ({ ...p, status: value }))}
                >
                    {QUOTATION_STATUSES_ENUM.map((s) => (
                        <Option key={s} value={s}>{convertToPresentableText(s)}</Option>
                    ))}
                </Select>
            </div>

            {/* Customer */}
            <div className="border rounded-lg p-3 space-y-3">
                <div className="text-xs font-semibold text-gray-600 uppercase">Customer (auto-filled from lead)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input label="Full Name" value={formData.customer.fullName} onChange={(e) => updateCustomer('fullName', e.target.value)} />
                    <Input label="Company" value={formData.customer.company} onChange={(e) => updateCustomer('company', e.target.value)} />
                    <Input label="Email" value={formData.customer.email} onChange={(e) => updateCustomer('email', e.target.value)} />
                    <Input label="Phone" value={formData.customer.phone} onChange={(e) => updateCustomer('phone', e.target.value)} />
                </div>
                <Input label="Address" value={formData.customer.address} onChange={(e) => updateCustomer('address', e.target.value)} />
            </div>

            {/* Line items */}
            <div className="border rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-gray-600 uppercase">Line Items</div>
                    <Button size="sm" variant="outlined" className="rounded-full flex items-center gap-1" onClick={addLineItem} type="button">
                        <PlusIcon size={14} /> Add Item
                    </Button>
                </div>

                {formData.lineItems.map((item, idx) => {
                    const lineTotal = computeLineTotal(item);
                    const isOffice = item.unit === QUOTATION_UNITS.OFFICE;
                    return (
                        <div key={idx} className="border rounded p-3 space-y-3 bg-gray-50">
                            <div className="flex items-start justify-between gap-2">
                                <div className="text-xs font-medium text-gray-500">Item {idx + 1}</div>
                                <IconButton size="sm" variant="text" color="red" onClick={() => removeLineItem(idx)} type="button">
                                    <Trash2Icon size={14} />
                                </IconButton>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                <Select
                                    label="Type"
                                    value={item.unit}
                                    onChange={(value) => updateLineItem(idx, 'unit', value)}
                                >
                                    {QUOTATION_UNITS_ENUM.map((u) => (
                                        <Option key={u} value={u}>{convertToPresentableText(u)}</Option>
                                    ))}
                                </Select>
                                <Input
                                    label="Quantity"
                                    type="number"
                                    min="0"
                                    value={item.quantity}
                                    onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                                />
                                <Input
                                    label={isOffice ? `Price per Seat (${currency})` : `Unit Price (${currency})`}
                                    type="number"
                                    min="0"
                                    value={item.unitPrice}
                                    onChange={(e) => updateLineItem(idx, 'unitPrice', e.target.value)}
                                />
                                {isOffice && (
                                    <Input
                                        label="Seats per office"
                                        type="number"
                                        min="0"
                                        value={item.seatsPerOffice}
                                        onChange={(e) => updateLineItem(idx, 'seatsPerOffice', e.target.value)}
                                    />
                                )}
                                <Input
                                    label="Area (e.g. 10x10, optional)"
                                    value={item.area}
                                    onChange={(e) => updateLineItem(idx, 'area', e.target.value)}
                                />
                            </div>
                            <div className="text-right text-sm font-semibold text-gray-700">
                                Line Total: {formatNumber(lineTotal, currency)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Pricing */}
            <div className="border rounded-lg p-3 space-y-3">
                <div className="text-xs font-semibold text-gray-600 uppercase">Pricing Summary</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Select
                        label="Currency"
                        value={formData.pricing.currency}
                        onChange={(value) => updatePricing('currency', value)}
                    >
                        {CURRENCIES_ENUM.map((c) => (
                            <Option key={c} value={c}>{c}</Option>
                        ))}
                    </Select>
                    <Input
                        label="Tax (%) — optional"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.pricing.taxPercent}
                        onChange={(e) => updatePricing('taxPercent', e.target.value)}
                    />
                    <Select
                        label="Discount Type"
                        value={formData.pricing.discount.type}
                        onChange={(value) => updateDiscount('type', value || '')}
                    >
                        <Option value="">None</Option>
                        <Option value="percent">Percent (%)</Option>
                        <Option value="flat">Flat Amount</Option>
                    </Select>
                    <Input
                        label="Discount Value"
                        type="number"
                        min="0"
                        value={formData.pricing.discount.value}
                        onChange={(e) => updateDiscount('value', e.target.value)}
                        disabled={!formData.pricing.discount.type}
                    />
                </div>
                <Input
                    label="Discount Reason (optional)"
                    value={formData.pricing.discount.reason}
                    onChange={(e) => updateDiscount('reason', e.target.value)}
                    disabled={!formData.pricing.discount.type}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                        label={`Security Deposit (${currency})`}
                        type="number"
                        min="0"
                        value={formData.pricing.securityDeposit}
                        onChange={(e) => updatePricing('securityDeposit', e.target.value)}
                    />
                    <Input
                        label={`Advance (${currency})`}
                        type="number"
                        min="0"
                        value={formData.pricing.advance}
                        onChange={(e) => updatePricing('advance', e.target.value)}
                    />
                </div>
                <div className="bg-gray-50 rounded p-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{formatNumber(subtotal, currency)}</span></div>
                    {discountAmount > 0 && (
                        <div className="flex justify-between text-red-600"><span>Discount</span><span>- {formatNumber(discountAmount, currency)}</span></div>
                    )}
                    {taxAmount > 0 && (
                        <div className="flex justify-between"><span>Tax ({Number(formData.pricing.taxPercent || 0)}%)</span><span>{formatNumber(taxAmount, currency)}</span></div>
                    )}
                    {Number(formData.pricing.securityDeposit) > 0 && (
                        <div className="flex justify-between"><span>Security Deposit</span><span>{formatNumber(formData.pricing.securityDeposit, currency)}</span></div>
                    )}
                    {Number(formData.pricing.advance) > 0 && (
                        <div className="flex justify-between text-red-600"><span>Advance</span><span>- {formatNumber(formData.pricing.advance, currency)}</span></div>
                    )}
                    <div className="flex justify-between font-bold text-base pt-2 border-t">
                        <span>Grand Total</span><span>{formatNumber(grandTotal, currency)}</span>
                    </div>
                </div>
            </div>

            {/* Terms */}
            <div className="border rounded-lg p-3 space-y-3">
                <div className="text-xs font-semibold text-gray-600 uppercase">Terms & Notes (auto-filled — edit per quotation if needed)</div>
                <Textarea
                    label="Inclusions"
                    value={formData.terms.inclusions}
                    onChange={(e) => updateTerms('inclusions', e.target.value)}
                    rows={8}
                />
                <Textarea
                    label="Terms & Conditions"
                    value={formData.terms.termsAndConditions}
                    onChange={(e) => updateTerms('termsAndConditions', e.target.value)}
                    rows={5}
                />
                <Textarea
                    label="Notes"
                    value={formData.terms.notes}
                    onChange={(e) => updateTerms('notes', e.target.value)}
                    rows={2}
                />
                <Textarea
                    label="Exclusions (optional)"
                    value={formData.terms.exclusions}
                    onChange={(e) => updateTerms('exclusions', e.target.value)}
                    rows={2}
                />
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                <Button variant="text" className="rounded-full" onClick={closeForm} type="button" disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="outlined"
                    className="rounded-full"
                    onClick={() => handleSave({ andDownload: false })}
                    type="button"
                    disabled={saving}
                >
                    {saving ? 'Saving...' : (editingId ? 'Update' : 'Save')}
                </Button>
                <Button
                    className="rounded-full flex items-center gap-1"
                    onClick={() => handleSave({ andDownload: true })}
                    type="button"
                    disabled={saving}
                >
                    <DownloadIcon size={14} /> Save & Download PDF
                </Button>
            </div>
            <ConfirmationDialog />
        </div>
    );
};

export default QuotationTab;
